import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ControlTypeModel, ImportTemplateModel, LineItemModel, TableDataTypeModel, TemplateModel, TemplateSectionModel, TemplateTypeModel, BusinessUnitModel, CloneTemplateRequest, ReferenceImageModel } from '../models/template.model';
import { ApiService } from '../core/services/api.service';
import { environment } from '../../environments/environment';

export interface TemplatePreviewExportData {
    template: TemplateModel;
    sections: TemplateSectionModel[];
}

@Injectable({ providedIn: 'root' })
export class TemplateService {
    private baseUrl = 'Template';

    constructor(private apiService: ApiService) { }

    getTableDataType(): Observable<TableDataTypeModel[]> {
        return this.apiService.get(`${this.baseUrl}/GetTableDataType`);
    }

    getControlType(): Observable<ControlTypeModel[]> {
        return this.apiService.get(`${this.baseUrl}/GetControlType`);
    }

    getTemplateTypeList(): Observable<TemplateTypeModel[]> {
        return this.apiService.get(`${this.baseUrl}/GetTemplateTypeList`);
    }

    getTemplateList(templateTypeId: number): Observable<TemplateModel[]> {
        return this.apiService.get(`${this.baseUrl}/GetTemplateList/${templateTypeId}`);
    }

    getSubTemplateList(): Observable<TemplateModel[]> {
        return this.apiService.get(`${this.baseUrl}/GetSubTemplateList`);
    }

    addTemplateType(template: any): Observable<number> {
        return this.apiService.post(`${this.baseUrl}/AddTemplateType`, template);
    }

    updateTemplateType(template: TemplateTypeModel): Observable<TemplateTypeModel> {
        return this.apiService.put(`${this.baseUrl}/UpdateTemplateType`, template);
    }

    deleteTemplateType(templateTypeId: number): Observable<any> {
        return this.apiService.delete(`${this.baseUrl}/DeleteTemplateType/${templateTypeId}`);
    }
    getTemplateById(templateId: number): Observable<TemplateModel> {
        return this.apiService.get(`${this.baseUrl}/GetTemplateById/${templateId}`);
    }

    addTemplate(template: any): Observable<number> {
        return this.apiService.post(`${this.baseUrl}/AddTemplate`, template);
    }

    cloneTemplate(request: CloneTemplateRequest): Observable<number> {
        return this.apiService.post(`${this.baseUrl}/CloneTemplate`, request);
    }

    updateTemplate(template: TemplateModel): Observable<TemplateModel> {
        return this.apiService.put(`${this.baseUrl}/UpdateTemplate`, template);
    }

    updateTemplateActive(templateId: number, isActive: boolean, updatedBy?: number): Observable<any> {
        return this.apiService.put(`${this.baseUrl}/UpdateTemplateActive/${templateId}`, { isActive, updatedBy });
    }

    deleteTemplate(templateId: number): Observable<any> {
        return this.apiService.delete(`${this.baseUrl}/DeleteTemplate/${templateId}`);
    }

    getTemplateSectionList(templateId: number): Observable<TemplateSectionModel[]> {
        return this.apiService.get(`${this.baseUrl}/GetTemplateSectionList/${templateId}`);
    }

    exportTemplatePreviewPdf(templateId: number): Observable<Blob> {
        return this.apiService.getBlob(`${this.baseUrl}/ExportTemplatePreviewPdf/${templateId}`);
    }

    getTemplatePreviewExportData(templateId: number, exportToken: string): Observable<TemplatePreviewExportData> {
        return this.apiService.get(`${this.baseUrl}/GetTemplatePreviewExportData/${templateId}?exportToken=${encodeURIComponent(exportToken)}`);
    }

    addTemplateSection(section: TemplateSectionModel): Observable<number> {
        return this.apiService.post(`${this.baseUrl}/AddTemplateSection`, section);
    }

    updateTemplateSection(section: TemplateSectionModel): Observable<TemplateSectionModel> {
        return this.apiService.put(`${this.baseUrl}/UpdateTemplateSection`, section);
    }

    deleteTemplateSection(sectionId: number): Observable<any> {
        return this.apiService.delete(`${this.baseUrl}/DeleteTemplateSection/${sectionId}`);
    }

    importTemplates(model:ImportTemplateModel): Observable<any> {       
        return this.apiService.post(`${this.baseUrl}/ImportTemplates`, model);
    }

    getTemplateLineItemList(sectionId: number): Observable<LineItemModel[]> {
        return this.apiService.get(`${this.baseUrl}/GetTemplateLineItemList/${sectionId}`);
    }

    getLineItemById(lineItemId: number): Observable<LineItemModel[]> {
        return this.apiService.get(`${this.baseUrl}/GetLineItemById/${lineItemId}`);
    }

    addLineItem(lineItem: LineItemModel): Observable<number> {
        return this.apiService.post(`${this.baseUrl}/AddLineItem`, lineItem);
    }

    uploadReferenceImages(templateId: number, files: File[]): Observable<ReferenceImageModel[]> {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file, file.name));
        return this.apiService.post(`${this.baseUrl}/UploadReferenceImages/${templateId}`, formData);
    }

    updateLineItem(lineItem: LineItemModel): Observable<LineItemModel> {
        return this.apiService.put(`${this.baseUrl}/UpdateLineItem`, lineItem);
    }

    deleteLineItem(lineItemId: number): Observable<any> {
        return this.apiService.delete(`${this.baseUrl}/DeleteLineItem/${lineItemId}`);
    }

    batchUpdateLineItemSortOrder(items: { lineItemId: number; sortOrder: number; updatedBy?: number }[]): Observable<any> {
        return this.apiService.post(`${this.baseUrl}/BatchUpdateLineItemSortOrder`, items);
    }

    batchUpdateTemplateSectionSortOrder(items: { sectionId: number; sortOrder: number; updatedBy?: number }[]): Observable<any> {
        return this.apiService.post(`${this.baseUrl}/BatchUpdateTemplateSectionSortOrder`, items);
    }

    getBusinessUnits(): Observable<BusinessUnitModel[]> {
        return this.apiService.get(`${this.baseUrl}/GetBusinessUnits`);
    }

    getTemplateRoles(templateId: number): Observable<number[]> {
        return this.apiService.get(`${this.baseUrl}/GetTemplateRoles/${templateId}`);
    }

    getFileUrl(path: string): string {
        return `${environment.cdnBaseUrl}?key=${encodeURIComponent(path)}`;
    }
}
